import React, { useState } from 'react';
import './TaintFlowGraph.css';

/* ── helpers ─────────────────────────────────────────────────────────────── */
const COLLAPSE_AT = 6;

function shortFile(path) {
  if (!path) return '—';
  const parts = path.replace(/\\/g, '/').split('/');
  return parts.length > 2 ? '…/' + parts.slice(-2).join('/') : path;
}

const ROLE_CFG = {
  source:    { icon: '⬤', label: 'SOURCE',       cls: 'source',    color: '#4f46e5' },
  sink:      { icon: '⬤', label: 'SINK',          cls: 'sink',      color: '#e11d48' },
  step:      { icon: '⬤', label: 'PROPAGATES',   cls: 'step',      color: '#64748b' },
  sanitizer: { icon: '⬤', label: 'SANITIZED',    cls: 'sanitizer', color: '#059669' },
};

function getRoleCfg(role, isSingleStep) {
  if (isSingleStep) return { icon: '⬤', label: 'MATCH LOCATION', cls: 'match', color: '#d97706' };
  return ROLE_CFG[role] || { icon: '⬤', label: (role || 'STEP').toUpperCase(), cls: 'step', color: '#64748b' };
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button className="tfg-copy-btn" onClick={copy} title={copied ? 'Copied!' : 'Copy code'}>
      {copied ? '✓ Copied' : '⧉ Copy'}
    </button>
  );
}

/* ── TaintFlowGraph ─────────────────────────────────────────────────────── */
export function TaintFlowGraph({ flowPath, ruleId, severity }) {
  const [collapsed, setCollapsed] = useState(true);

  if (!flowPath || flowPath.length === 0) return null;

  const isSingleStep = flowPath.length === 1;
  const tooLong = flowPath.length > COLLAPSE_AT;
  const shouldCollapse = tooLong && collapsed;
  const hiddenCount = flowPath.length - 4;
  const isXFile = !isSingleStep && flowPath.some(s => s?.file !== flowPath[0]?.file);

  const visibleWithIdx = shouldCollapse
    ? [
        [0, flowPath[0]],
        [1, flowPath[1]],
        [-1, null],
        [flowPath.length - 2, flowPath[flowPath.length - 2]],
        [flowPath.length - 1, flowPath[flowPath.length - 1]],
      ]
    : flowPath.map((s, i) => [i, s]);

  const source = flowPath[0];
  const sink   = flowPath[flowPath.length - 1];

  return (
    <div className="tfg-root">

      {/* ── Header bar ──────────────────────────────────────────────────── */}
      <div className="tfg-header">
        <div className="tfg-header-left">
          <span className="tfg-header-icon">🔗</span>
          <span className="tfg-header-title">
            {isSingleStep ? 'Pattern Match Location' : 'Taint Flow Path'}
          </span>
          <span className="tfg-badge tfg-badge--nodes">
            {flowPath.length} {flowPath.length === 1 ? 'node' : 'nodes'}
          </span>
          {isXFile && <span className="tfg-badge tfg-badge--xfile">⚡ Cross-file</span>}
        </div>
        {ruleId && (
          <span className="tfg-header-rule" title={ruleId}>{ruleId}</span>
        )}
      </div>

      {/* ── Flow summary (multi-step) ────────────────────────────────────── */}
      {!isSingleStep && (
        <div className="tfg-summary">
          <span className="tfg-sum-chip tfg-sum-chip--source">
            <span className="tfg-sum-dot" style={{ background: '#4f46e5' }} />
            <span className="tfg-sum-file">{shortFile(source?.file)}</span>
            {source?.line > 0 && <span className="tfg-sum-line">:{source.line}</span>}
          </span>
          {flowPath.length > 2 && (
            <span className="tfg-sum-mid">
              {flowPath.length - 2} intermediate {flowPath.length - 2 === 1 ? 'step' : 'steps'}
            </span>
          )}
          <span className="tfg-sum-arrow">→</span>
          <span className="tfg-sum-chip tfg-sum-chip--sink">
            <span className="tfg-sum-dot" style={{ background: '#e11d48' }} />
            <span className="tfg-sum-file">{shortFile(sink?.file)}</span>
            {sink?.line > 0 && <span className="tfg-sum-line">:{sink.line}</span>}
          </span>
        </div>
      )}

      {/* ── Single-step note ────────────────────────────────────────────── */}
      {isSingleStep && (
        <div className="tfg-match-note">
          📍 Joern identified this pattern at a single location — no inter-procedural data flow propagation detected.
        </div>
      )}

      {/* ── Step nodes ──────────────────────────────────────────────────── */}
      <div className="tfg-flow">
        {visibleWithIdx.map(([origIdx, step], renderIdx) => {

          /* hidden sentinel */
          if (step === null) {
            return (
              <React.Fragment key="sentinel">
                <div className="tfg-connector">
                  <div className="tfg-connector-track" />
                  <div className="tfg-connector-arrow" />
                </div>
                <button className="tfg-expand-btn" onClick={() => setCollapsed(false)}>
                  ··· {hiddenCount} hidden intermediate {hiddenCount === 1 ? 'step' : 'steps'} — click to expand ···
                </button>
                <div className="tfg-connector">
                  <div className="tfg-connector-track" />
                  <div className="tfg-connector-arrow" />
                </div>
              </React.Fragment>
            );
          }

          const stepNum = origIdx + 1;
          const cfg     = getRoleCfg(step.role, isSingleStep);
          const prevEntry  = renderIdx > 0 ? visibleWithIdx[renderIdx - 1] : null;
          const prevStep   = prevEntry ? prevEntry[1] : null;
          const isCrossFile = prevStep && prevStep.file && step.file && prevStep.file !== step.file;

          return (
            <React.Fragment key={`n-${origIdx}`}>

              {/* Connector */}
              {renderIdx > 0 && prevStep !== null && !isSingleStep && (
                <div className={`tfg-connector${isCrossFile ? ' tfg-connector--xfile' : ''}`}>
                  {isCrossFile && (
                    <div className="tfg-xfile-badge">
                      📂 crosses into {shortFile(step.file)}
                    </div>
                  )}
                  <div className="tfg-connector-track" />
                  <div className="tfg-connector-arrow" />
                  {!isCrossFile && (
                    <span className="tfg-connector-label">taint propagates</span>
                  )}
                </div>
              )}

              {/* Node card */}
              <div className={`tfg-node tfg-node--${cfg.cls}`}>

                {/* Node header */}
                <div className={`tfg-node-head tfg-node-head--${cfg.cls}`}>
                  <div className="tfg-node-head-left">
                    <span className="tfg-role-pill" style={{ '--rc': cfg.color }}>
                      <span className="tfg-role-dot" style={{ background: cfg.color }} />
                      {cfg.label}
                    </span>
                    {step.method && (
                      <span className="tfg-method-chip" title="Enclosing method">
                        fn {step.method}()
                      </span>
                    )}
                  </div>
                  <div className="tfg-node-head-right">
                    <span className="tfg-loc" title={step.file}>
                      <span className="tfg-loc-file">{shortFile(step.file)}</span>
                      {step.line > 0 && (
                        <span className="tfg-loc-line">:{step.line}</span>
                      )}
                    </span>
                    {!isSingleStep && (
                      <span className="tfg-step-num">{stepNum} / {flowPath.length}</span>
                    )}
                  </div>
                </div>

                {/* Code area */}
                {step.code && (
                  <div className="tfg-code-area">
                    <div className="tfg-gutter">
                      {step.line > 0 && (
                        <span className="tfg-gutter-num">{step.line}</span>
                      )}
                    </div>
                    <div className="tfg-code-body">
                      <span className="tfg-code-marker" style={{ color: cfg.color }}>›</span>
                      <code className={`tfg-code tfg-code--${cfg.cls}`}>{step.code}</code>
                    </div>
                    <CopyButton text={step.code} />
                  </div>
                )}

              </div>
            </React.Fragment>
          );
        })}

        {tooLong && !collapsed && (
          <button className="tfg-collapse-btn" onClick={() => setCollapsed(true)}>
            ▲ Collapse path
          </button>
        )}
      </div>

      {/* ── Legend (multi-step only) ─────────────────────────────────────── */}
      {!isSingleStep && (
        <div className="tfg-legend">
          {Object.entries(ROLE_CFG).map(([role, cfg]) => (
            <span key={role} className={`tfg-legend-chip tfg-legend-chip--${cfg.cls}`}>
              <span style={{ color: cfg.color }}>⬤</span> {cfg.label}
            </span>
          ))}
        </div>
      )}

    </div>
  );
}

/* ── PatternContextCard ─────────────────────────────────────────────────── */
export function PatternContextCard({ captures, context, file, line }) {
  const hasCaptures = captures && Object.keys(captures).length > 0;
  const hasContext  = context && (context.function || context.class || context.module);
  if (!hasCaptures && !hasContext) return null;

  return (
    <div className="pcc-root">
      <div className="pcc-header">
        <span>📦</span>
        <span>Match Context</span>
      </div>

      {hasContext && (
        <div className="pcc-location">
          {context.module   && <span className="pcc-crumb pcc-module">{context.module}</span>}
          {context.module   && context.class && <span className="pcc-sep">›</span>}
          {context.class    && <span className="pcc-crumb pcc-class">{context.class}</span>}
          {(context.module || context.class) && context.function && <span className="pcc-sep">›</span>}
          {context.function && <span className="pcc-crumb pcc-function">{context.function}()</span>}
          {line > 0         && <><span className="pcc-sep">@</span><span className="pcc-line">line {line}</span></>}
        </div>
      )}

      {hasCaptures && (
        <div className="pcc-captures">
          <div className="pcc-captures-title">Metavariable bindings</div>
          <div className="pcc-captures-grid">
            {Object.entries(captures).map(([key, val]) => (
              <div key={key} className="pcc-capture-row">
                <code className="pcc-cap-key">{key}</code>
                <span className="pcc-cap-eq">=</span>
                <code className="pcc-cap-val">{val}</code>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
